'use strict';

// external imports

// shared imports
import LocalizableError from '~/shared/src/base/LocalizableError';
import metaModel from '~/shared/src/utilities/metaModel';
import {__} from '~/shared/src/utilities/messageCatalog';
import dbDefinitionMapper from '~/backend/src/connectors/db/dbDefinitionMapper';
import Status from '~/shared/src/mm/Status';
import PropertySet from '~/shared/src/mm/PropertySet';
import ReferenceSet from '~/shared/src/mm/ReferenceSet';

// backend imports


// type definitions
export type ItemTypeAttributes = {
    id? : number,
    name? : string,
    statusId? : number,
    origin? : string,
    module? : string,
    description? : string,
    keyDescription? : string,
    keyDefinition? : string,
    nameDescription? : string,
    nameDefinition? : string,
    expiryDays? : number,
    displayField? : 'CI_KEY' | 'CI_NAME',
    isKeyAutogenerated? : boolean,
    isImported? : boolean,
    uuid? : string,
    modifierUserId? : string,
    modificationTime? : NDate,
    validFromTime? : NDate,
    validToTime? : NDate
};

export type ItemTypeAttributesCollection = Array<ItemTypeAttributes>;

export type ItemTypeAttributesRequired = keyof Pick<ItemTypeAttributes, 'id' | 'name' | 'statusId'>;

export type ItemTypeAttributesSettable = Pick<ItemTypeAttributes, 'name' | 'statusId' | 'origin' | 'module' | 'description' | 'keyDescription' | 'keyDefinition' | 'nameDescription' | 'nameDefinition' | 'expiryDays' | 'displayField' | 'isKeyAutogenerated' | 'isImported'>;

/**
 * The ItemType class.
 */
class ItemType {

    /**
     * The unique Id counter for ItemTypes
     */
    static uniqueIdCount = -1;


    /*** ATTRIBUTES ********************************************************************************************************************/

    /**
     * The ID of the ItemType
     */
    readonly id : number;

    /**
     * The Name of the ItemType
     */
    protected _name : string = '';
    get name() {
        return this._name;
    }

    set name( value : string ) {
        this.validateName( value );

        this.handleModified( 'name', value );

        this._name = value;

        // TODO: how to handle UUID's update (ItemType, PropertySets, ReferenceSets, ReferencePropertySets)
    }

    /**
     * The StatusID of the ItemType
     */
    protected _statusId : number;
    get statusId() : number {
        return this._statusId;
    }

    set statusId( value : number ) {
        this.validateStatusId( value );

        this.handleModified( 'statusId', value );

        this._statusId = value;
    }

    /**
     * The origin of this
     */
    protected _origin : string = 'FSZ';
    get origin() : string {
        return this._origin;
    }

    set origin( value : string ) {
        this.validateOrigin( value );

        this.handleModified( 'origin', value );

        this._origin = value;
    }

    /**
     * The module this belongs to
     * @protected
     */
    protected _module : string = 'base';
    get module() : string {
        return this._origin;
    }

    set module( value : string ) {
        this.validateModule( value );

        this.handleModified( 'module', value );

        this._module = value;
    }

    /**
     * The description of the ItemType
     */
    protected _description : string = '';
    get description() {
        return this._description;
    }

    set description( value : string ) {
        this.checkStatus();

        this.handleModified( 'description', value );

        this._description = value;
    }

    /**
     * The description of the key of an Item of the ItemType
     */
    protected _keyDescription : string = '';
    get keyDescription() {
        return this._keyDescription;
    }

    set keyDescription( value : string ) {
        this.checkStatus();

        this.handleModified( 'keyDescription', value );

        this._keyDescription = value;
    }

    /**
     * The definition of the key of an Item of the ItemType
     */
    protected _keyDefinition : string = '';
    get keyDefinition() {
        return this._keyDefinition;
    }

    set keyDefinition( value : string ) {
        this.checkStatus();

        this.handleModified( 'keyDefinition', value );

        this._keyDefinition = value;
    }

    /**
     * The description of the name of an Item of the ItemType
     */
    protected _nameDescription : string = '';
    get nameDescription() {
        return this._nameDescription;
    }

    set nameDescription( value : string ) {
        this.checkStatus();

        this.handleModified( 'nameDescription', value );

        this._nameDescription = value;
    }

    /**
     * The definition of the name of an Item of the ItemType
     */
    protected _nameDefinition : string = '';
    get nameDefinition() {
        return this._nameDefinition;
    }

    set nameDefinition( value : string ) {
        this.checkStatus();

        this.handleModified( 'nameDefinition', value );

        this._nameDefinition = value;
    }

    /**
     * The number of days after an Item of the ItemType will be expired by the importer
     * Only applicable if isImported for the ItemType is set to true
     */
    protected _expiryDays : number = 5;
    get expiryDays() {
        return this._expiryDays;
    }

    set expiryDays( value : number ) {
        this.checkStatus();

        this.handleModified( 'expiryDays', value );

        this._expiryDays = value;
    }

    /**
     * The dbField to display for an Item of the ItemType
     */
    protected _displayField : 'CI_KEY' | 'CI_NAME' = 'CI_KEY';
    get displayField() {
        return this._displayField;
    }

    set displayField( value : 'CI_KEY' | 'CI_NAME' ) {
        this.checkStatus();

        this.handleModified( 'displayField', value );

        this._displayField = value;
    }

    /**
     * Is the key of an Item of the ItemType automatically generated
     */
    protected _isKeyAutogenerated : boolean = true;
    get isKeyAutogenerated() {
        return this._isKeyAutogenerated;
    }

    set isKeyAutogenerated( value : boolean ) {
        this.checkStatus();

        this.handleModified( 'isKeyAutogenerated', value );

        this._isKeyAutogenerated = value;
    }

    /**
     * Is the ItemType imported from another datasource
     */
    protected _isImported : boolean = false;
    get isImported() {
        return this._isImported;
    }

    set isImported( value : boolean ) {
        this.checkStatus();

        this.handleModified( 'isImported', value );

        this._isImported = value;
    }

    /**
     * The unique spoken id of the ItemType
     */
    readonly uuid : string = '';

    /**
     * The UserID of the last modifier of the ItemType
     */
    readonly modifierUserId : string = '';

    /**
     * The last modification date of the ItemType
     */
    readonly modificationTime : NDate = null;

    /**
     * The valid from date of a History-ItemType
     */
    readonly validFromTime : NDate = null;

    /**
     * The valid to date of a History-ItemType
     */
    readonly validToTime : NDate = null;


    /*** ALIASES ***********************************************************************************************************************/

    get status() : Status {
        return metaModel[ this._schema ].getStatus( this._statusId )!;
    }

    /**
     * The attribute to display an Item of this ItemType with
     */
    get displayAttribute() : 'key' | 'name' {
        let retVal : 'key' | 'name' = 'key';

        const itemDbFields = dbDefinitionMapper.schemas[ this.schema ].views.item.dbFields;

        if ( itemDbFields[ this._displayField ] !== undefined ) {
            retVal = itemDbFields[ this._displayField ] as 'key' | 'name';
        }

        return retVal;
    }

    /**
     * The icon for this
     */
    get icon() : string {
        return metaModel[ this._schema ].getImage( 'itemType', this.id, 'icon' ) ?? 'fa fa-shapes';
    }

    /**
     * The label for this
     */
    get labelMsgId() : string {
        let retVal = metaModel[ this._schema ].getMessage( 'itemType', this.id, 'typeLabel' );

        if ( !retVal || retVal === __( retVal ) ) {
            retVal = this._name;
        }

        return retVal;
    }

    /**
     * The label for this
     */
    get label() : string {
        return __( this.labelMsgId );
    }

    /**
     * The key label for this
     */
    get keyLabelMsgId() : string {
        let retVal = metaModel[ this._schema ].getMessage( 'itemType', this.id, 'keyLabel' );

        if ( !retVal || retVal === __( retVal ) ) {
            retVal = 'YN.base.common.item.key';
        }

        return retVal;
    }

    /**
     * The key label for this
     */
    get keyLabel() : string {
        return __( this.keyLabelMsgId );
    }

    /**
     * The key label for this
     */
    get nameLabelMsgId() : string {
        let retVal = metaModel[ this._schema ].getMessage( 'itemType', this.id, 'nameLabel' );

        if ( !retVal || retVal === __( retVal ) ) {
            retVal = 'YN.base.common.item.name';
        }

        return retVal;
    }

    /**
     * The name label for this
     */
    get nameLabel() : string {
        return __( this.nameLabelMsgId );
    }

    /**
     * The PropertySets for this
     */
    get propertySets() : PropertySet[] {
        return metaModel[ this._schema ].getPropertySetsForItemType( this.id );
    }

    /**
     * The ReferenceSets for this
     */
    get referenceSets() : ReferenceSet[] {
        return metaModel[ this._schema ].getReferenceSetsForItemType( this.id );
    }


    /*************************************************************************************************************************************/

    /**
     * The database schema this Item belongs to
     */
    protected _schema : Schema;
    get schema() {
        return this._schema;
    }

    /**
     * Map containing the modified attributes with their initial values
     */
    protected _attributesModified : Map<keyof ItemTypeAttributesSettable, AttributeValue>;
    get attributesModified() {
        return this._attributesModified;
    }

    /**
     * The constructor.
     *
     * @param attributes                An object containing attribute-value pairs for this
     * @param schema                    The database schema the Item belongs to
     */
    constructor( attributes : ItemTypeAttributes, schema : Schema = global.schema ) {

        if ( attributes.id && attributes.id < 1 ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attributes.validation.id.invalid' );
        }

        this._schema = schema;

        if ( attributes.id ) {
            this.id = attributes.id;
        }
        else {
            this.id = ItemType.uniqueIdCount--;
        }

        if ( attributes.statusId ) {
            this._statusId = attributes.statusId;
        }
        else {
            this._statusId = metaModel[ schema ].getStatus( 'active' )!.id;
        }

        if ( attributes.origin ) {
            this._origin = attributes.origin;
        }

        if ( attributes.module ) {
            this._module = attributes.module;
        }

        if ( attributes.name ) {
            this._name = attributes.name;
        }

        if ( attributes.description ) {
            this._description = attributes.description;
        }

        if ( attributes.keyDescription ) {
            this._keyDescription = attributes.keyDescription;
        }
        if ( attributes.keyDefinition ) {
            this._keyDefinition = attributes.keyDefinition;
        }

        if ( attributes.nameDescription ) {
            this._nameDescription = attributes.nameDescription;
        }
        if ( attributes.nameDefinition ) {
            this._nameDefinition = attributes.nameDefinition;
        }

        if ( attributes.expiryDays ) {
            this._expiryDays = attributes.expiryDays;
        }

        if ( attributes.displayField ) {
            this._displayField = attributes.displayField;
        }

        if ( attributes.isKeyAutogenerated !== undefined ) {
            this._isKeyAutogenerated = attributes.isKeyAutogenerated;
        }

        if ( attributes.isImported !== undefined ) {
            this._isImported = attributes.isImported;
        }

        if ( attributes.uuid ) {
            this.uuid = attributes.uuid;
        }

        if ( attributes.modifierUserId ) {
            this.modifierUserId = attributes.modifierUserId;
        }
        if ( attributes.modificationTime ) {
            this.modificationTime = attributes.modificationTime;
        }

        if ( attributes.validFromTime ) {
            this.validFromTime = attributes.validFromTime;
        }
        if ( attributes.validToTime ) {
            this.validToTime = attributes.validToTime;
        }

        this._attributesModified = new Map();
    }

    /**
     * Returns an object containing the attributes with their values limited by includeAttributes parameter.
     *
     * @param includeAttributes   The state of the attributes to include in result.
     */
    toJSON( includeAttributes : 'all' | 'modified' = 'all' ) : ItemTypeAttributes {
        // required attributes
        const retVal : ItemTypeAttributes = {
            id: this.id
        };

        if ( includeAttributes === 'all' || this._attributesModified.has( 'name' ) ) {
            retVal.name = this._name;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'statusId' ) ) {
            retVal.statusId = this._statusId;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'origin' ) ) {
            retVal.origin = this._origin;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'module' ) ) {
            retVal.module = this._module;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'description' ) ) {
            retVal.description = this._description;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'keyDescription' ) ) {
            retVal.keyDescription = this._keyDescription;
        }
        if ( includeAttributes === 'all' || this._attributesModified.has( 'keyDefinition' ) ) {
            retVal.keyDefinition = this._keyDefinition;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'nameDescription' ) ) {
            retVal.nameDescription = this._nameDescription;
        }
        if ( includeAttributes === 'all' || this._attributesModified.has( 'nameDefinition' ) ) {
            retVal.nameDefinition = this._nameDefinition;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'expiryDays' ) ) {
            retVal.expiryDays = this._expiryDays;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'displayField' ) ) {
            retVal.displayField = this._displayField;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'isKeyAutogenerated' ) ) {
            retVal.isKeyAutogenerated = this._isKeyAutogenerated;
        }

        if ( includeAttributes === 'all' || this._attributesModified.has( 'isImported' ) ) {
            retVal.isImported = this._isImported;
        }

        // not settable attributes
        if ( includeAttributes === 'all' ) {
            retVal.uuid = this.uuid;
            retVal.modifierUserId = this.modifierUserId;

            // only active attributes
            if ( !this.isHistorized() ) {
                retVal.modificationTime = this.modificationTime;
            }

            // only history attributes
            else {
                retVal.validFromTime = this.validFromTime;
                retVal.validToTime = this.validToTime;
            }
        }

        return retVal;
    }

    /**
     * Builds and returns the signature string for this
     */
    toString() : string {
        return `ItemType '${this.name}' (ID: ${this.id})`;
    }

    /**
     * Resets all settable attributes to their initial values.
     */
    reset() {
        if ( this._attributesModified.has( 'name' ) ) {
            this._name = this._attributesModified.get( 'name' ) as string;
        }

        if ( this._attributesModified.has( 'statusId' ) ) {
            this._statusId = this._attributesModified.get( 'statusId' ) as number;
        }

        if ( this._attributesModified.has( 'description' ) ) {
            this._description = this._attributesModified.get( 'description' ) as string;
        }

        if ( this._attributesModified.has( 'keyDescription' ) ) {
            this._keyDescription = this._attributesModified.get( 'keyDescription' ) as string;
        }
        if ( this._attributesModified.has( 'keyDefinition' ) ) {
            this._keyDefinition = this._attributesModified.get( 'keyDefinition' ) as string;
        }

        if ( this._attributesModified.has( 'nameDescription' ) ) {
            this._nameDescription = this._attributesModified.get( 'nameDescription' ) as string;
        }
        if ( this._attributesModified.has( 'nameDefinition' ) ) {
            this._nameDefinition = this._attributesModified.get( 'nameDefinition' ) as string;
        }

        if ( this._attributesModified.has( 'expiryDays' ) ) {
            this._expiryDays = this._attributesModified.get( 'expiryDays' ) as number;
        }

        if ( this._attributesModified.has( 'displayField' ) ) {
            this._displayField = this._attributesModified.get( 'displayField' ) as 'CI_KEY' | 'CI_NAME';
        }

        if ( this._attributesModified.has( 'isKeyAutogenerated' ) ) {
            this._isKeyAutogenerated = this._attributesModified.get( 'isKeyAutogenerated' ) as boolean;
        }

        if ( this._attributesModified.has( 'isImported' ) ) {
            this._isImported = this._attributesModified.get( 'isImported' ) as boolean;
        }

        this._attributesModified = new Map();
    }

    /**
     * Creates and returns a clone of this with given attributes applied.
     *
     * @param [attributes]          An object containing attribute-value pairs to be applied to the clone
     */
    clone( attributes : ItemTypeAttributesSettable = {} ) : ItemType {
        const clone : ItemType = Object.assign( {}, this );

        // apply data
        if ( attributes.name ) {
            clone.name = attributes.name;
        }
        if ( attributes.statusId ) {
            clone.statusId = attributes.statusId;
        }
        if ( attributes.description ) {
            clone.description = attributes.description;
        }
        if ( attributes.keyDescription ) {
            clone.keyDescription = attributes.keyDescription;
        }
        if ( attributes.keyDefinition ) {
            clone.keyDefinition = attributes.keyDefinition;
        }
        if ( attributes.nameDescription ) {
            clone.nameDescription = attributes.nameDescription;
        }
        if ( attributes.nameDefinition ) {
            clone.nameDefinition = attributes.nameDefinition;
        }
        if ( attributes.expiryDays ) {
            clone.expiryDays = attributes.expiryDays;
        }
        if ( attributes.displayField ) {
            clone.displayField = attributes.displayField;
        }
        if ( attributes.isKeyAutogenerated ) {
            clone.isKeyAutogenerated = attributes.isKeyAutogenerated;
        }
        if ( attributes.isImported ) {
            clone.isImported = attributes.isImported;
        }

        return clone;
    }

    /**
     * Checks if this is new.
     */
    isNew() : boolean {
        return this.id < 0;
    }

    /**
     * Checks if this is historized.
     */
    isHistorized() : boolean {
        return this.validFromTime !== null;
    }

    /**
     * Checks if this is deleted.
     */
    isDeleted() : boolean {
        return this._statusId === metaModel[ this._schema ].getStatus( 'history' )?.id || this._statusId === metaModel[ this._schema ].getStatus( 'deleted' )?.id;
    }

    /**
     * Checks if this or an EnumProperty or a Property of this is modified.
     */
    isModified() : boolean {
        return this._attributesModified.size > 0;
    }

    /**
     * Checks if the given attribute is modified or not.
     *
     * @param attribute        The attribute to check if it is modified
     */
    isAttributeModified( attribute : keyof ItemTypeAttributesSettable ) : boolean {
        return this._attributesModified.has( attribute );
    }

    /**
     * Handles the modified attributes list.
     *
     * @param attribute         The attribute
     * @param value             The value for the attribute
     */
    protected handleModified( attribute : keyof ItemTypeAttributesSettable, value : AttributeValue ) {
        // set as modified
        if ( !this._attributesModified.has( attribute ) && value !== this[ attribute ] ) {
            this._attributesModified.set( attribute, this[ attribute ] );
        }

        // unset as modified
        else if ( this._attributesModified.has( attribute ) && value === this._attributesModified.get( attribute ) ) {
            this._attributesModified.delete( attribute );
        }
    }

    protected checkStatus() {
        if ( this.isDeleted() ) {
            throw new LocalizableError(
                'YN.base.dataObject.itemType.attributes.validation.couldNotChangeDeleted'
            );
        }
    }

    /**
     * Validates the given statusId
     */
    protected validateStatusId( statusId : number ) {
        if ( !metaModel[ this._schema ].getStatus( statusId ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.statusId.notExists' );
        }

        if (
            (
                statusId === metaModel[ this._schema ].getStatus( 'history' )?.id
                || statusId === metaModel[ this._schema ].getStatus( 'deleted' )?.id
            )
            && (
                this._attributesModified.size > 1
                || ( this._attributesModified.size === 1 && !this._attributesModified.has( 'statusId' ) )
            )
        ) {
            throw new LocalizableError(
                'YN.base.dataObject.item.attributes.validation.couldNotDeleteChanged'
            );
        }
    }

    /**
     * Validates the origin for this
     */
    protected validateOrigin( origin : string ) {
        this.checkStatus();

        if ( !origin.match( /^[A-Z]+$/ ) ) {
            throw new LocalizableError( 'YN.base.dataObject.common.attribute.validation.origin.isNotUppercase' );
        }
    }

    /**
     * Validates the module for this
     */
    protected validateModule( module : string ) {
        this.checkStatus();
    }

    /**
     * Validates the name for this
     */
    protected validateName( name : string ) {
        this.checkStatus();

        const itemType = metaModel[ this._schema ].getItemType( name );

        if ( itemType && itemType.id !== this.id ) {
            throw new LocalizableError( 'YN.base.dataObject.itemType.attribute.validation.name.isNotUnique' );
        }
    }

}

export default ItemType;
